import {
    EVENT_POSTRENDER_LAYER,
    EVENT_PRERENDER_LAYER,
    LAYERID_DEPTH,
    SORTMODE_CUSTOM,
    BoundingBox,
    CameraComponent,
    Color,
    Entity,
    Layer,
    GraphicsDevice,
    MeshInstance,
    Vec3,
    WireRenderer
} from 'playcanvas';

import { AssetLoader } from './asset-loader';
import { Camera } from './camera';
import { CameraPoseGizmos } from './camera-pose-gizmos';
import { CommandQueue } from './command-queue';
import { DataProcessor } from './data-processor';
import { Element, ElementType, ElementTypeList } from './element';
import { Events } from './events';
import { InfiniteGrid as Grid } from './infinite-grid';
import { Outline } from './outline';
import { PCApp } from './pc-app';
import { ProjectedSplatRenderer } from './projected-splat-renderer';
import { SceneConfig } from './scene-config';
import { SceneState } from './scene-state';
import { Splat } from './splat';
import { Underlay } from './underlay';

// sort meshInstances by the aabb corner furthest from the camera
const corner = new Vec3();
const specialSort = (instances: MeshInstance[], numInstances: number, cameraPos: Vec3, cameraDir: Vec3) => {
    const distances = new Map<MeshInstance, number>();

    for (let i = 0; i < numInstances; i++) {
        const instance = instances[i];
        const { aabb } = instance;
        const { center, halfExtents } = aabb;

        // loop over all 8 aabb corners and find the furthest distance along the camera view direction
        let maxDist = -Infinity;
        for (let cx = -1; cx <= 1; cx += 2) {
            for (let cy = -1; cy <= 1; cy += 2) {
                for (let cz = -1; cz <= 1; cz += 2) {
                    corner.set(
                        center.x + cx * halfExtents.x,
                        center.y + cy * halfExtents.y,
                        center.z + cz * halfExtents.z
                    );
                    // project camera-to-corner vector onto camera direction
                    const dist = (corner.x - cameraPos.x) * cameraDir.x +
                                    (corner.y - cameraPos.y) * cameraDir.y +
                                    (corner.z - cameraPos.z) * cameraDir.z;
                    if (dist > maxDist) {
                        maxDist = dist;
                    }
                }
            }
        }

        // store in map for reuse during sort
        distances.set(instance, maxDist);
    }

    // sort instances back-to-front by calculated distance (furthest first)
    instances.sort((a, b) => distances.get(b) - distances.get(a));
};

// rendered frames of timing history kept for the performance overlay. The
// overlay plots all of them but summarises only the most recent second's worth,
// so this is graph history rather than the averaging window.
const FRAME_TIMING_WINDOW = 180;

class Scene {
    events: Events;
    config: SceneConfig;
    canvas: HTMLCanvasElement;
    app: PCApp;
    wire: WireRenderer;
    worldLayer: Layer;
    splatLayer: Layer;
    overlayLayer: Layer;
    centersLayer: Layer;
    gizmoLayer: Layer;
    sceneState = [new SceneState(), new SceneState()];
    elements: Element[] = [];
    boundStorage = new BoundingBox();
    boundDirty = true;
    forceRender = false;

    // True while the user is dragging in the viewport. The element-state diff below
    // only sees what elements serialize, so edits that live outside it - a
    // per-gaussian transform (transform palette + instance list), a selection
    // change - are indistinguishable from a settled scene. Rather than have each
    // tool remember to report itself, this is driven from the pointer directly, so
    // every tool is treated the same. It classifies frames only: it never forces a
    // frame to render that wouldn't have.
    forceInteracting = false;

    // motion-adaptive stochastic rendering. While the scene is actively changing
    // (camera orbit / edit) we draw the fast no-sort stochastic mode; when it
    // settles we render a single clean sorted & blended frame. movingRender picks
    // the mode for the current frame; pendingResolve marks that a clean settled
    // frame is still owed after motion ends.
    movingRender = false;

    // the overdraw diagnostic view (view.overdraw): the splat pass counts
    // fragments per pixel and the final blit maps the count through a heat
    // ramp. Always the sorted path - the count needs blending, not the
    // stochastic depth test. It is a debug overlay, so it follows the camera's
    // overlay flag: always in the viewport, in captures only with Show Debug
    // Overlays, never for flood selection's offscreen read or 360 captures
    overdrawRender = false;
    pendingResolve = false;
    // anything but the camera changed this frame: the projected renderer's
    // occlusion cull skips a frame whose previous depth no longer describes
    // the scene (see ProjectedSplatRenderer.render)
    editedRender = false;

    // 'auto' stochastic mode follows the timing of the last rendered sorted
    // frame: engaged (stochastic-during-movement) while that frame's GPU span
    // exceeded autoEngageMs, disengaged once a later sorted frame - at minimum
    // the clean resolve frame on every settle - comes in under it, so hiding
    // content or moving to a lighter view drops back to sorted. Stochastic
    // frames say nothing about the cost of sorting, so they never update the
    // decision: profiler reports resolve asynchronously, and frameModes
    // records each profiled frame's mode by renderVersion so reports can be
    // attributed (see onGpuReport). The threshold is tweakable from the
    // console as `scene.autoEngageMs = 5` to exercise the switch on scenes
    // that are not actually slow.
    autoEngageMs = 60;
    autoEngaged = false;
    private autoSampling = false;
    private frameModes = new Map<number, boolean>();

    lockedRenderMode = false;
    lockedRender = false;

    // viewport rendering is suspended while a document load applies its pieces:
    // layers become visible before their saved transforms arrive and the camera
    // pose is restored last, so intermediate frames would show a half-assembled
    // scene. Set by doc.ts, which forces a render once the load completes.
    suspendRender = false;

    // > 0 while a pick batch owns the camera's colour and work buffers as its
    // depth/id targets: a frame rendered between its passes and readbacks
    // would overwrite them, and since the targets are rebuilt from onPreRender
    // a held frame also keeps their dimensions fixed for the batch. Counted
    // and released by Camera, which forces a render when the hold ends.
    renderHold = 0;

    // Console controls for stochastic rendering; setters request a repaint.
    readonly stochastic;

    canvasResize: {width: number; height: number} | null = null;
    targetSize = {
        width: 0,
        height: 0
    };

    // rolling window of per-frame timings for the performance overlay, sampled
    // only on frames that actually rendered so the window is never padded with
    // frames that did not happen. gpu is the profiler's frame span
    // (earliest pass begin to latest pass end); the engine reports the span
    // rather than the sum of per-pass durations because pass intervals overlap
    // on pipelined GPUs, which makes the sum grow with the pass count even
    // while the GPU is idle. The timestamp readback is asynchronous, so the
    // same value can be sampled on consecutive frames - harmless for the
    // min/median/p95 the overlay derives from the window.
    readonly frameTimings = {
        gpu: [] as number[],
        cpu: [] as number[],
        width: 0,
        height: 0,
        stochastic: false,
        gpuSupported: false
    };

    private cpuFrameStart = 0;

    dataProcessor: DataProcessor;
    projectedSplatRenderer: ProjectedSplatRenderer;
    assetLoader: AssetLoader;
    camera: Camera;
    cameraPoseGizmos: CameraPoseGizmos;
    grid: Grid;
    outline: Outline;
    underlay: Underlay;

    // shared queue for serialising async splat work. exposed so subsystems that
    // need to order their async work alongside edit-history operations can do so
    // without going through edit-history directly.
    commandQueue: CommandQueue;

    contentRoot: Entity;
    cameraRoot: Entity;

    constructor(
        events: Events,
        config: SceneConfig,
        canvas: HTMLCanvasElement,
        graphicsDevice: GraphicsDevice,
        commandQueue: CommandQueue
    ) {
        this.events = events;
        this.config = config;
        this.canvas = canvas;
        this.commandQueue = commandQueue;

        let resolve = true;
        let warp = false;
        let warpStrength = 1.5;
        let undistort = true;
        const repaint = () => {
            this.forceRender = true;
        };
        this.stochastic = {
            set resolve(value: boolean) {
                resolve = value;
                repaint();
            },
            get resolve() {
                return resolve;
            },
            set warp(value: boolean) {
                warp = value;
                repaint();
            },
            get warp() {
                return warp;
            },
            // Centre magnification: 1 = identity, 2 = 2x per axis.
            set warpStrength(value: number) {
                warpStrength = Math.max(1, Math.min(2, value));
                repaint();
            },
            get warpStrength() {
                return warpStrength;
            },
            // Disable to inspect the warped sampling layout before undistortion.
            set undistort(value: boolean) {
                undistort = value;
                repaint();
            },
            get undistort() {
                return undistort;
            }
        };

        // configure the playcanvas application. we render to an offscreen buffer so require
        // only the simplest of backbuffers.
        this.app = new PCApp(canvas, { graphicsDevice });
        this.wire = new WireRenderer(this.app);

        // only render the scene when instructed
        this.app.autoRender = false;
        // @ts-ignore
        this.app._allowResize = false;

        // hack: disable lightmapper first bake until we expose option for this
        // @ts-ignore
        this.app.off('prerender', this.app._firstBake, this.app);

        // @ts-ignore
        this.app.loader.getHandler('texture').imgParser.crossOrigin = 'anonymous';

        // this is required to get full res AR mode backbuffer
        this.app.graphicsDevice.maxPixelRatio = window.devicePixelRatio;

        // configure application canvas
        const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
            if (entries.length > 0) {
                const entry = entries[0];
                if (entry) {
                    if (entry.devicePixelContentBoxSize) {
                        // on non-safari browsers, we are given the pixel-perfect canvas size
                        this.canvasResize = {
                            width: entry.devicePixelContentBoxSize[0].inlineSize,
                            height: entry.devicePixelContentBoxSize[0].blockSize
                        };
                    } else if (entry.contentBoxSize.length > 0) {
                        // on safari browsers we must calculate pixel size from CSS size ourselves
                        // and hope the browser performs the same calculation.
                        const pixelRatio = window.devicePixelRatio;
                        this.canvasResize = {
                            width: Math.ceil(entry.contentBoxSize[0].inlineSize * pixelRatio),
                            height: Math.ceil(entry.contentBoxSize[0].blockSize * pixelRatio)
                        };
                    }
                }
                this.forceRender = true;
            }
        });

        const canvasContainer = window.document.getElementById('canvas-container');
        observer.observe(canvasContainer);

        // Uniform "user is dragging" signal for every tool. canvasContainer is the
        // common ancestor of the canvas (where the engine's gizmos listen) and of
        // tools-container (where the 2d selection tools listen), and the capture
        // phase sees the event before any of them - including through a
        // setPointerCapture retarget. `buttons` keeps hovering out of it, so no
        // per-tool drag state is needed.
        canvasContainer.addEventListener('pointermove', (event: PointerEvent) => {
            if (event.buttons !== 0) {
                this.forceInteracting = true;
            }
        }, true);

        // configure depth layers to handle dynamic refraction
        const depthLayer = this.app.scene.layers.getLayerById(LAYERID_DEPTH);
        this.app.scene.layers.remove(depthLayer);
        this.app.scene.layers.insertOpaque(depthLayer, 2);

        // register application callbacks
        this.app.on('update', (deltaTime: number) => this.onUpdate(deltaTime));
        this.app.on('prerender', () => this.onPreRender());
        this.app.on('postrender', () => this.onPostRender());

        this.frameTimings.gpuSupported = !!(this.app.graphicsDevice as any).supportsTimestampQuery;
        events.function('scene.frameTimings', () => this.frameTimings);

        // the profiler's report callback carries the renderVersion of the frame
        // the timings belong to, but the engine discards it before the value
        // lands in _frameTime. Wrap it so reports can be attributed to the mode
        // the frame rendered with (see onGpuReport).
        const profiler = this.app.graphicsDevice.gpuProfiler as any;
        const originalReport = profiler.report.bind(profiler);
        profiler.report = (renderVersion: number, timings: number[] | null, frameTime?: number) => {
            originalReport(renderVersion, timings, frameTime);
            this.onGpuReport(renderVersion, timings, frameTime);
        };

        // force render on device restored
        this.app.graphicsDevice.on('devicerestored', () => {
            this.forceRender = true;
        });

        // fire pre and post render events on the camera
        this.app.scene.on(EVENT_PRERENDER_LAYER, (camera: CameraComponent, layer: Layer, transparent: boolean) => {
            camera.fire('preRenderLayer', layer, transparent);
        });

        this.app.scene.on(EVENT_POSTRENDER_LAYER, (camera: CameraComponent, layer: Layer, transparent: boolean) => {
            camera.fire('postRenderLayer', layer, transparent);
        });

        // get the world layer
        this.worldLayer = this.app.scene.layers.getLayerByName('World');
        this.wire.layer = this.worldLayer;

        // splat layer - dedicated layer for splat rendering with MRT
        this.splatLayer = new Layer({
            name: 'Splat',
            opaqueSortMode: SORTMODE_CUSTOM,
            transparentSortMode: SORTMODE_CUSTOM
        });
        this.splatLayer.customCalculateSortValues = specialSort;

        // tool overlay layer - drawn after the splats (e.g. ghost passes of the
        // measure/orient tool overlays, which show through occluding gaussians)
        this.overlayLayer = new Layer({ name: 'ToolOverlay' });

        // splat centers - depth tested and written against a cleared buffer, so
        // overlapping centers resolve nearest-first and the gaussians never
        // occlude them. Its own layer because the gizmo shapes share the gizmo
        // layer's depth buffer, and some of them write a constant fragment depth
        // (see the engine's gizmo unlitShader) which no ordering can reconcile
        // with real per-center depth
        this.centersLayer = new Layer({
            name: 'Centers',
            clearDepthBuffer: true
        });

        // gizmo layer - clear scene depth before drawing gizmos so they remain visible
        this.gizmoLayer = new Layer({
            name: 'Gizmo',
            clearDepthBuffer: true,
            clearStencilBuffer: true
        });

        const layers = this.app.scene.layers;
        layers.push(this.splatLayer);
        layers.push(this.overlayLayer);
        layers.push(this.centersLayer);
        layers.push(this.gizmoLayer);

        this.projectedSplatRenderer = new ProjectedSplatRenderer(this);
        this.dataProcessor = new DataProcessor(this.app.graphicsDevice);
        this.assetLoader = new AssetLoader(this.app, events);

        // create root entities
        this.contentRoot = new Entity('contentRoot');
        this.app.root.addChild(this.contentRoot);

        this.cameraRoot = new Entity('cameraRoot');
        this.app.root.addChild(this.cameraRoot);

        // create elements
        this.camera = new Camera();
        this.add(this.camera);

        this.cameraPoseGizmos = new CameraPoseGizmos();
        this.add(this.cameraPoseGizmos);

        this.grid = new Grid();
        this.add(this.grid);

        this.outline = new Outline();
        this.add(this.outline);
        this.underlay = new Underlay();
        this.add(this.underlay);
    }

    start() {
        // start the app
        this.app.start();
    }

    clear() {
        const splats = this.getElementsByType(ElementType.splat);
        splats.forEach((splat) => {
            (splat as Splat).destroy();
        });
    }

    // elements removed while their add() was still awaiting element.add().
    // Element lifecycles aren't cancellation safe (a Splat's add awaits a
    // bounds readback and then dereferences its scene), so the removal is
    // deferred until the add settles instead of pulling the scene out from
    // under it
    private pendingRemovals = new Set<Element>();

    // add a scene element
    async add(element: Element) {
        if (element.scene === this && this.pendingRemovals.has(element)) {
            // re-added while the earlier add is still in flight: cancel the
            // deferred removal and let that add register it
            this.pendingRemovals.delete(element);
            return;
        }

        if (!element.scene) {
            // add the new element
            element.scene = this;
            await element.add();

            // removed while adding: it was never registered or announced, so
            // just tear it down
            if (this.pendingRemovals.delete(element)) {
                element.remove();
                element.scene = null;
                return;
            }

            this.elements.push(element);

            // notify all elements of scene addition
            this.forEachElement(e => e !== element && e.onAdded(element));

            // notify listeners
            this.events.fire('scene.elementAdded', element);
        }
    }

    // remove an element from the scene
    remove(element: Element) {
        if (element.scene === this) {
            // not registered yet: add() is still awaiting element.add(), so
            // defer the removal to its continuation (see pendingRemovals)
            const index = this.elements.indexOf(element);
            if (index === -1) {
                this.pendingRemovals.add(element);
                return;
            }
            this.elements.splice(index, 1);

            // notify listeners
            this.events.fire('scene.elementRemoved', element);

            // notify all elements of scene removal
            this.forEachElement(e => e.onRemoved(element));

            element.remove();
            element.scene = null;
        }
    }

    // get the scene bound
    get bound() {
        if (this.boundDirty) {
            let valid = false;
            this.forEachElement((e) => {
                const bound = e.worldBound;
                if (bound) {
                    if (!valid) {
                        valid = true;
                        this.boundStorage.copy(bound);
                    } else {
                        this.boundStorage.add(bound);
                    }
                }
            });

            this.boundDirty = false;
            this.events.fire('scene.boundChanged', this.boundStorage);
        }

        return this.boundStorage;
    }

    getElementsByType(elementType: ElementType) {
        return this.elements.filter(e => e.type === elementType);
    }

    get graphicsDevice() {
        return this.app.graphicsDevice;
    }

    get warpedRender() {
        return this.movingRender && this.stochastic.warp && this.stochastic.warpStrength > 1;
    }

    private forEachElement(action: (e: Element) => void) {
        this.elements.forEach(action);
    }

    private onUpdate(deltaTime: number) {
        this.cpuFrameStart = performance.now();

        if (this.canvasResize) {
            this.canvas.width = this.canvasResize.width;
            this.canvas.height = this.canvasResize.height;
            this.canvasResize = null;
        }

        // allow elements to update
        this.forEachElement(e => e.onUpdate(deltaTime));

        // fire global update
        this.events.fire('update', deltaTime);

        // fire a 'serialize' event which listers will use to store their state. we'll use
        // this to decide if the view has changed and so requires rendering.
        const i = this.app.frame % 2;
        const state = this.sceneState[i];
        state.reset();
        this.forEachElement(e => state.pack(e));

        // diff with previous state
        const result = state.compare(this.sceneState[1 - i]);

        // generate the set of all element types that changed
        const all = new Set([...result.added, ...result.removed, ...result.moved, ...result.changed]);

        // the performance overlay only advances on frames that render, so keep
        // rendering while it is on. It deliberately does not count as
        // interaction: settled frames stay on the sorted path, which is what
        // makes the two render modes comparable by starting and stopping a drag.
        const profiling = !!this.events.invoke('view.perfOverlay');

        // compare with previously serialized
        const changed = this.forceRender || profiling || all.size > 0;
        const interacting = this.forceInteracting || all.size > 0;
        // per-gaussian edits reach here only through forceRender; layer moves,
        // visibility and grade changes through the state diff
        this.editedRender = this.forceRender || [...all].some(type => type !== ElementType.camera);
        const stochastic = this.events.invoke('view.stochastic');

        // 'movement' takes the fast no-sort stochastic path only while actively
        // interacting, so settled frames get the clean sorted & blended one;
        // 'enabled' stays stochastic even once the scene settles. 'auto' acts
        // like 'movement' while the last sorted frame's GPU span exceeded
        // autoEngageMs and like 'disabled' otherwise (updated per report in
        // onGpuReport); without timestamp-query support it can never measure,
        // so it falls back to 'movement' rather than leave heavy scenes janky.
        // Locked-mode captures always use the sorted path.
        const auto = stochastic === 'auto';
        this.autoSampling = auto && this.frameTimings.gpuSupported;
        const adaptive = stochastic === 'movement' ||
            (auto && (this.autoEngaged || !this.frameTimings.gpuSupported));
        this.overdrawRender = this.camera.renderOverlays && !!this.events.invoke('view.overdraw');
        this.movingRender = !this.lockedRenderMode && !this.overdrawRender &&
            (stochastic === 'enabled' || (adaptive && interacting));

        // timestamp queries cost a per-frame staging-buffer map and a resolve,
        // so run the profiler only while something consumes it: the
        // frame-timings overlay or auto-mode sampling. Locked-mode frames are
        // never timed: reports resolve asynchronously, so a slow
        // high-resolution capture span could otherwise land in _frameTime
        // after unlock and be mistaken for an editor frame (disabling also
        // zeroes the report, so nothing stale survives the capture).
        // Stochastic frames are timed too: the renderer's contribution cull
        // adapts to their span (see onGpuReport).
        this.app.graphicsDevice.gpuProfiler.enabled =
            (profiling || this.autoSampling || this.movingRender) && !this.lockedRenderMode;

        if (this.suspendRender || this.renderHold > 0) {
            this.app.renderNextFrame = false;
        } else if (this.lockedRenderMode) {
            this.app.renderNextFrame = this.lockedRender;
            this.lockedRender = false;
        } else if (!this.app.renderNextFrame) {
            if (changed) {
                this.app.renderNextFrame = true;
                // motion in 'movement' mode owes one clean sorted frame on settle
                if (interacting) {
                    this.pendingResolve = adaptive;
                }
            } else if (this.pendingResolve) {
                // scene just settled → render the single clean resolve frame
                this.pendingResolve = false;
                this.app.renderNextFrame = true;
            }
        }
        this.forceRender = false;
        this.forceInteracting = false;

        // raise per-type update events
        ElementTypeList.forEach((type) => {
            if (all.has(type)) {
                this.events.fire(`updated:${type}`);
            }
        });

        // allow elements to postupdate
        this.forEachElement(e => e.onPostUpdate());
    }

    private onPreRender() {
        // update render target size (config.camera.pixelScale divides the
        // backbuffer size; the final blit upscales the result to fill the screen)
        this.targetSize.width = Math.ceil(this.app.graphicsDevice.width / this.config.camera.pixelScale);
        this.targetSize.height = Math.ceil(this.app.graphicsDevice.height / this.config.camera.pixelScale);

        this.forEachElement(e => e.onPreRender());

        this.projectedSplatRenderer.render();

        this.events.fire('prerender', this.camera.displayTransform);

        // debug - display scene bound
        if (this.config.debug.showBound) {
            // draw element bounds
            this.forEachElement((e: Element) => {
                if (e.type === ElementType.splat) {
                    const splat = e as Splat;

                    const local = splat.localBound;
                    this.wire.color = Color.RED;
                    this.wire.transform = splat.entity.getWorldTransform();
                    this.wire.boxMinMax(local.getMin(), local.getMax());

                    const world = splat.worldBound;
                    this.wire.color = Color.GREEN;
                    this.wire.transform = null;
                    this.wire.boxMinMax(world.getMin(), world.getMax());
                }
            });

            // draw scene bound
            this.wire.color = Color.BLUE;
            this.wire.boxMinMax(this.bound.getMin(), this.bound.getMax());
        }
    }

    private onPostRender() {
        this.forEachElement(e => e.onPostRender());

        this.events.fire('postrender');

        const gpuTime = (this.app.graphicsDevice.gpuProfiler as any)?._frameTime ?? 0;

        // record the mode this frame rendered with, keyed by its renderVersion,
        // so the frame's asynchronously resolving profiler report can be
        // attributed in onGpuReport. Only profiled frames get reports (this
        // also excludes locked-mode frames, which are never timed - see
        // onUpdate), so only those are recorded.
        const device = this.app.graphicsDevice as any;
        if (device.gpuProfiler._enabled) {
            this.frameModes.set(device.renderVersion, this.movingRender);
        }

        const timings = this.frameTimings;
        timings.gpu.push(gpuTime);
        timings.cpu.push(performance.now() - this.cpuFrameStart);
        if (timings.gpu.length > FRAME_TIMING_WINDOW) {
            timings.gpu.shift();
            timings.cpu.shift();
        }
        timings.width = this.targetSize.width;
        timings.height = this.targetSize.height;
        timings.stochastic = this.movingRender;
    }

    // handle an asynchronously resolved gpu timing report. Only sorted frames
    // measure the cost 'auto' mode trades away, so only their spans update the
    // engage decision; stochastic-frame spans drive the renderer's motion cull
    // instead. timings is null when the backend discards a frame (e.g. a
    // disjoint timer event).
    private onGpuReport(renderVersion: number, timings: number[] | null, frameTime?: number) {
        const moving = this.frameModes.get(renderVersion);

        // reports resolve in order, so drop this frame's entry and any older
        // ones a discarded report left behind
        this.frameModes.forEach((_, version) => {
            if (version <= renderVersion) {
                this.frameModes.delete(version);
            }
        });

        if (moving === undefined || !timings || timings.length === 0) {
            return;
        }
        const gpuTime = frameTime ?? timings.reduce((sum, t) => sum + t, 0);
        if (moving) {
            this.projectedSplatRenderer.reportStochasticFrame(gpuTime);
        } else {
            this.autoEngaged = gpuTime > this.autoEngageMs;
        }
    }
}

export { SceneConfig, Scene };
